/* eslint-disable no-restricted-imports */
import React, {useState} from 'react'
import styles from './UIGameEndContent.module.scss'
import clsx from 'clsx'
import {
    DevCardStats,
    DiceStats,
    Overview,
    ResourceCardStats,
    ActivityStats,
    ResourceStats,
} from './tabs'
import {PlayerTable} from './PlayerTable'
import {useUIGameEndContext} from '../../../contexts/UIGameEndContext'
import {T} from '../../../common/T'
import {TranslationObject} from '@colonist/i18n'
import {FADE_IN_ANIMATION_DELAYS} from '../constants'
import {Sound} from '../../../utils/sound'
import {FadeInContainer} from '../../../common/FadeInContainer'
import {CA} from '../../../../../../code/client/shared/scripts/analytics/Analytics'
import {useGameStore} from '../../../store/GameStoreContext'
import {useIsReplay} from '../../../store/selectors/useIsReplay'

type Tab = 'overview' | 'dice-stats' | 'res-card-stats' | 'dev-card-stats' | 'activity-stats' | 'resource-stats'

interface TabData {
    id: Tab
    label: TranslationObject
    analyticEvent: CA.Label
}

const TAB_CONTENT_MAP: Record<Tab, React.ReactNode> = {
    overview: <Overview />,
    'dice-stats': <DiceStats />,
    'res-card-stats': <ResourceCardStats />,
    'dev-card-stats': <DevCardStats />,
    'activity-stats': <ActivityStats />,
    'resource-stats': <ResourceStats />,
}

export const Tabs = ({
    tabs,
    selected,
    onTabClick,
}: {
    tabs: TabData[]
    selected: string
    onTabClick: (tab: TabData) => void
}) => <div className={styles.tabs}>
    {tabs.map((tab) => (
        <div
            className={clsx(styles.tab, {[styles.active]: selected === tab.id})}
            key={tab.id}
            onClick={() => {
                onTabClick(tab)
                Sound.playSound(Sound.SoundType.Click)
            }}
        >
            <T id={tab.label} />
        </div>
    ))}
</div>

export const UIGameEndContent = ({disableAnimation}: {disableAnimation: boolean}) => {
    const {isPlayerHasRankedState} = useUIGameEndContext()
    const isReplay = useIsReplay()
    const translationKeyContext = useGameStore((state) => state.gameMode.translationKeyContext)

    const TAB_DATA_OVERVIEW: TabData = {
        id: 'overview',
        label: {key: 'strings:game.endGame.overview.title'},
        analyticEvent: CA.Label.ClickedEndGameOverview,
    }
    const TAB_DATA_DICE_STATS: TabData = {
        id: 'dice-stats',
        label: {key: 'strings:game.endGame.dice.title'},
        analyticEvent: CA.Label.ClickedEndGameDiceStats,
    }
    const TAB_DATA_RESOURCE_CARD_STATS: TabData = {
        id: 'res-card-stats',
        label: {key: 'strings:game.endGame.resourceCards.title'},
        analyticEvent: CA.Label.ClickedEndGameResourceCardStats,
    }
    const TAB_DATA_DEV_CARD_STATS: TabData = {
        id: 'dev-card-stats',
        label: {
            key: 'strings:game.endGame.devCards.title',
            options: {context: translationKeyContext},
        },
        analyticEvent: CA.Label.ClickedEndGameDevelopmentCardStats,
    }
    const TAB_DATA_ACTIVITY_STATS: TabData = {
        id: 'activity-stats',
        label: {key: 'strings:game.endGame.activity.title'},
        analyticEvent: CA.Label.ClickedEndGameActivityStats,
    }
    const TAB_DATA_RESOURCE_STATS: TabData = {
        id: 'resource-stats',
        label: {key: 'strings:game.endGame.resource.title'},
        analyticEvent: CA.Label.ClickedEndGameResourceStats,
    }

    const TOP_TAB_DATA: TabData[] = [TAB_DATA_OVERVIEW, TAB_DATA_ACTIVITY_STATS, TAB_DATA_RESOURCE_STATS]
    const BOTTOM_TAB_DATA: TabData[] = [TAB_DATA_DICE_STATS, TAB_DATA_RESOURCE_CARD_STATS, TAB_DATA_DEV_CARD_STATS]
    const ALL_TAB_DATA: TabData[] = [
        TAB_DATA_OVERVIEW,
        TAB_DATA_DICE_STATS,
        TAB_DATA_RESOURCE_CARD_STATS,
        TAB_DATA_DEV_CARD_STATS,
        TAB_DATA_ACTIVITY_STATS,
        TAB_DATA_RESOURCE_STATS,
    ]

    const [selectedTab, setSelectedTab] = useState(ALL_TAB_DATA[0].id)
    const [selectedTopTab, setSelectedTopTab] = useState(TOP_TAB_DATA[0].id)
    const [selectedBottomTab, setSelectedBottomTab] = useState(BOTTOM_TAB_DATA[0].id)

    const handleTabClick = (tab: TabData) => {
        setSelectedTab(tab.id)
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, tab.analyticEvent)
    }

    const handleTopTabClick = (tab: TabData) => {
        setSelectedTopTab(tab.id)
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, tab.analyticEvent)
    }

    const handleBottomTabClick = (tab: TabData) => {
        setSelectedBottomTab(tab.id)
        CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, tab.analyticEvent)
    }

    return (
        <FadeInContainer
            animationDelayMs={isPlayerHasRankedState && !isReplay ? FADE_IN_ANIMATION_DELAYS.contentRankedGame : FADE_IN_ANIMATION_DELAYS.content}
            animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
            className={clsx(styles.contentContainer)}
            disableAnimation={disableAnimation}>
            <div className={styles.scaleContainer}>
                <div className={styles.allTabsContainer}>
                    <Tabs
                        onTabClick={(tab) => handleTabClick(tab)}
                        selected={selectedTab}
                        tabs={ALL_TAB_DATA}
                    />
                    <div className={styles.tabContent}>
                        <PlayerTable />
                        {TAB_CONTENT_MAP[selectedTab]}
                    </div>
                </div>
                <div className={styles.topTabsContainer}>
                    <Tabs
                        onTabClick={(tab) => handleTopTabClick(tab)}
                        selected={selectedTopTab}
                        tabs={TOP_TAB_DATA}
                    />
                    <div className={styles.tabContent}>
                        <PlayerTable />
                        {TAB_CONTENT_MAP[selectedTopTab]}
                    </div>
                </div>
                <div className={styles.bottomTabsContainer}>
                    <Tabs
                        onTabClick={(tab) => handleBottomTabClick(tab)}
                        selected={selectedBottomTab}
                        tabs={BOTTOM_TAB_DATA}
                    />
                    <div className={styles.tabContent}>
                        {TAB_CONTENT_MAP[selectedBottomTab]}
                    </div>
                </div>
            </div>
        </FadeInContainer>
    )
}
